import { As2PartnerDirection, IAs2Partner } from '@core/models/as2-message.model';
import { Supplier } from '@core/models/supplier.model';
import { IUser } from '@core/models/user-listing.model';
import { getSuppliersThunk } from '@redux/features/supplier.slice';
import { getAllUsersThunk } from '@redux/features/user-listing.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import as2IntegrationService from 'services/api/as2-integration.service';
import integrationsService from 'services/api/integrations.service';
import {
    downloadReferenceCertificate,
    validateCertFile,
} from '../Integrations/cert-upload.helpers';
import { integrationErrorMessage } from '../Integrations/integration-user-messages';
import { canReceiveAs2Partner, canSendAs2Partner } from './as2-partner.utils';

interface Props {
    open: boolean;
    existing?: IAs2Partner | null;
    onClose: () => void;
    onSaved: () => void;
}

const inputClass =
    'w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#bd902e] focus:outline-none focus:ring-2 focus:ring-[#bd902e]/30 disabled:bg-slate-50';

function getUsersPageContent(raw: unknown): IUser[] {
    const u = raw as Record<string, unknown> | null | undefined;
    if (!u) return [];
    const inner = (u.usersWithPage as Record<string, unknown>) || u;
    return (inner.content as IUser[]) || [];
}

function pharmacyLabel(u: IUser): string {
    const name = u.account?.businessName?.trim();
    return name ? `${name} (${u.email})` : u.email;
}

const As2PartnerModal: FC<Props> = ({ open, existing, onClose, onSaved }) => {
    const [form, setForm] = useState<Partial<IAs2Partner>>({});
    const [loading, setLoading] = useState(false);
    const [certFile, setCertFile] = useState<File | null>(null);
    const [uploadingCert, setUploadingCert] = useState(false);

    const reduxDispatch = useAppDispatch();
    const usersRaw = useAppSelector((s: RootState) => s.userListingState.users);
    const { suppliers } = useAppSelector((s: RootState) => (s as RootState).supplierState || { suppliers: [] });
    const pharmacyUsers = useMemo(
        () => getUsersPageContent(usersRaw).filter((u) => u.account?.accountType === 'PHARMACY'),
        [usersRaw]
    );

    const direction = (form.direction || 'BIDIRECTIONAL') as As2PartnerDirection;

    useEffect(() => {
        if (open) {
            reduxDispatch(getAllUsersThunk({ pageNumber: '0', pageSize: '500', accountType: 'PHARMACY' }));
            reduxDispatch(getSuppliersThunk());
        }
    }, [open, reduxDispatch]);

    useEffect(() => {
        if (existing) {
            setForm({
                ...existing,
                direction: (existing.direction || 'BIDIRECTIONAL') as As2PartnerDirection,
            });
        }
        if (!open) setCertFile(null);
    }, [existing, open]);

    if (!open || !existing?.code) return null;

    const set = (field: keyof IAs2Partner, value: unknown) =>
        setForm((f) => ({ ...f, [field]: value }));

    const save = async () => {
        if (!form.name?.trim()) {
            toast.error('Display name is required');
            return;
        }
        if (!form.as2Id?.trim()) {
            toast.error('AS2 ID is required');
            return;
        }
        setLoading(true);
        try {
            await as2IntegrationService.updatePartner(existing.code!, form);
            toast.success(`Partner ${existing.code} updated`);
            onSaved();
            onClose();
        } catch {
            /* interceptor */
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[1065] flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-[6vh] sm:pt-[8vh]">
            <div className="flex max-h-[min(85vh,900px)] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="shrink-0 border-b border-slate-200 px-6 py-4">
                    <h3 className="text-lg font-bold text-slate-900">Edit partner {existing.code}</h3>
                    <p className="mt-1 text-sm text-slate-500">
                        Update URLs, links, and contact details. OpenAS2 route names are fixed at creation;
                        partnerships.xml is regenerated automatically on save — do not edit that file manually.
                    </p>
                </div>
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
                    <div>
                        <label className="mb-1 block text-sm font-medium">Direction *</label>
                        <select
                            className={inputClass}
                            value={direction}
                            onChange={(e) => set('direction', e.target.value as As2PartnerDirection)}
                        >
                            <option value="RECEIVE">RECEIVE</option>
                            <option value="SEND">SEND</option>
                            <option value="BIDIRECTIONAL">BIDIRECTIONAL</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">Display name</label>
                        <input
                            className={inputClass}
                            value={form.name || ''}
                            onChange={(e) => set('name', e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">Contact email</label>
                        <input
                            className={inputClass}
                            value={form.contactEmail || ''}
                            onChange={(e) => set('contactEmail', e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">Partner AS2 ID *</label>
                        <input
                            className={inputClass}
                            value={form.as2Id || ''}
                            onChange={(e) => set('as2Id', e.target.value)}
                        />
                    </div>
                    {canSendAs2Partner(direction) && (
                        <>
                            <div>
                                <label className="mb-1 block text-sm font-medium">Partner AS2 URL *</label>
                                <input
                                    className={inputClass}
                                    value={form.as2Url || ''}
                                    onChange={(e) => set('as2Url', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium">Outbound route</label>
                                <input
                                    className={inputClass}
                                    readOnly
                                    title="Set by Integration wizard; cannot be changed here"
                                    value={form.partnershipOutbound || ''}
                                />
                            </div>
                        </>
                    )}
                    {canReceiveAs2Partner(direction) && (
                        <div>
                            <label className="mb-1 block text-sm font-medium">Inbound route</label>
                            <input
                                className={inputClass}
                                readOnly
                                title="Set by Integration wizard; cannot be changed here"
                                value={form.partnershipInbound || ''}
                            />
                        </div>
                    )}
                    <div>
                        <label className="mb-1 block text-sm font-medium">OpenAS2 partner name</label>
                        <input
                            className={inputClass}
                            readOnly
                            title="Set by Integration wizard; cannot be changed here"
                            value={form.openas2PartnerName || ''}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">Keystore alias</label>
                        <input
                            className={inputClass}
                            value={form.keystoreAlias || ''}
                            onChange={(e) => set('keystoreAlias', e.target.value)}
                        />
                    </div>
                    {canSendAs2Partner(direction) && (
                        <div>
                            <label className="mb-1 block text-sm font-medium">Linked pharmacy *</label>
                            <select
                                className={inputClass}
                                value={form.pharmacyUserId != null ? String(form.pharmacyUserId) : ''}
                                onChange={(e) =>
                                    set('pharmacyUserId', e.target.value ? Number(e.target.value) : undefined)
                                }
                            >
                                <option value="">— None —</option>
                                {pharmacyUsers.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {pharmacyLabel(u)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {canReceiveAs2Partner(direction) && (
                        <div>
                            <label className="mb-1 block text-sm font-medium">Linked supplier *</label>
                            <select
                                className={inputClass}
                                value={form.supplierId != null ? String(form.supplierId) : ''}
                                onChange={(e) =>
                                    set('supplierId', e.target.value ? Number(e.target.value) : undefined)
                                }
                            >
                                <option value="">— None —</option>
                                {(suppliers || []).map((s: Supplier) => (
                                    <option key={s.id} value={s.id}>
                                        {s.businessName || s.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={!!form.enabled}
                            onChange={(e) => set('enabled', e.target.checked)}
                        />
                        Enabled
                    </label>
                    {canSendAs2Partner(direction) && (
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={!!form.autoSendOnShip}
                                onChange={(e) => set('autoSendOnShip', e.target.checked)}
                            />
                            Auto-send on ship
                        </label>
                    )}
                    <div className="rounded-md border border-slate-200 p-3">
                        <p className="mb-2 text-xs font-medium text-slate-700">Partner certificate</p>
                        <button
                            type="button"
                            className="mb-2 text-xs font-semibold text-[#bd902e] hover:underline"
                            onClick={() => downloadReferenceCertificate(existing.code!)}
                        >
                            Download reference certificate
                        </button>
                        <input
                            type="file"
                            accept=".cer,.pem,.crt,.p12,.pfx"
                            className="block w-full text-xs"
                            onChange={(e) => setCertFile(e.target.files?.[0] || null)}
                        />
                        <button
                            type="button"
                            disabled={!certFile || uploadingCert}
                            className="mt-2 rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                            onClick={async () => {
                                const err = validateCertFile(certFile);
                                if (err) {
                                    toast.error(err, { autoClose: 8000 });
                                    return;
                                }
                                setUploadingCert(true);
                                try {
                                    await integrationsService.uploadCertificate(existing.code!, certFile!);
                                    toast.success(`Certificate uploaded for ${existing.code}`, { autoClose: 5000 });
                                    setCertFile(null);
                                    onSaved();
                                } catch (uploadErr) {
                                    toast.error(
                                        integrationErrorMessage(uploadErr, 'Could not upload certificate.'),
                                        { autoClose: 8000 }
                                    );
                                } finally {
                                    setUploadingCert(false);
                                }
                            }}
                        >
                            {uploadingCert ? 'Uploading…' : 'Upload certificate'}
                        </button>
                    </div>
                </div>
                <div className="mt-auto flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
                    <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={loading}
                        className="rounded-md bg-[#bd902e] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                        {loading ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default As2PartnerModal;
